/**
 * Primitive decoders for values that arrive without a usable type: route query, runtime config,
 * CMS fields, parsed JSON. Decoding is concentrated here so callers branch on a domain value
 * instead of re-testing representations at each use.
 */

export const isString = (value: unknown): value is string => typeof value === 'string'

export const isNumber = (value: unknown): value is number => typeof value === 'number'

/** A non-null, non-array object whose properties are still untyped. */
export const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

/** Interpolatable field values; objects would render as "[object Object]". */
export const isScalar = (value: unknown): value is string | number | boolean =>
  ['string', 'number', 'boolean'].includes(typeof value)

/** Narrows to a usable string, discarding empty strings, `null` and non-strings alike. */
export const asString = (value: unknown): string | undefined => (isString(value) && value ? value : undefined)

/**
 * vue-router gives every query value as `string | null | (string | null)[]`, so a caller that wants
 * one value takes the first usable string.
 */
export const queryString = (value: unknown): string | undefined =>
  Array.isArray(value) ? asString(value[0]) : asString(value)

/** The same query value read as a list, so single and repeated params are handled the same way. */
export const queryStrings = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [value]).filter(isString).filter(Boolean)
